<script lang="ts" setup>
import { ref } from 'vue';
import { useClipboard } from '@vueuse/core';
import { useMutation } from '@vue/apollo-composable';
import { UserApiKeyCreateDocument } from '@/gql/graphql';
import { genApiKey } from '@/utils/api-utils';
import {
  BAlert,
  BButton,
  BInputGroup,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';

// --- Emit "apiKeyCreated" when new access token created ---
const emits = defineEmits(['apiKeyCreated', 'apiKeyDismissed']);

// --- Local state for new API token creation ---
const newTokenName = ref<string>('');
const newTokenValue = ref<string>('');
const newTokenDescription = ref<string>('');
const newTokenPermissions = ref<string>('read');
const newTokenExpiration = ref<string | null>(null);

const creatingToken = ref(false);
const createTokenError = ref<string | null>(null);
const createTokenSuccess = ref(false);
const createTokenShowSuccessIndicator = ref(false);

const createdTokenName = ref<string | null>(null);
const createdTokenValue = ref<string | null>(null);

const showCopyTokenTooltip = ref(false);

// --- Mutation to create a new API token ---
const {
  mutate: createApiKey,
  onDone: onCreateTokenDone,
  onError: onCreateTokenError,
} = useMutation(UserApiKeyCreateDocument);

// When the create mutation returns, show a success message and refetch tokens.
onCreateTokenDone((res) => {
  if (res.data && res.data.userApiKeyCreate) {
    createTokenError.value = null;
    createTokenShowSuccessIndicator.value = true;
    createTokenSuccess.value = true;

    createdTokenName.value = newTokenName.value;
    createdTokenValue.value = newTokenValue.value;

    setTimeout(() => {
      createTokenShowSuccessIndicator.value = false;
    }, 2000);

    newTokenName.value = '';
    newTokenValue.value = '';
    newTokenDescription.value = '';
    newTokenExpiration.value = null;

    emits('apiKeyCreated'); // Emit event to parent component to refetch access tokens
  } else {
    console.error('Token creation failed, missing token data:', res.data);
    createTokenSuccess.value = false;
    createTokenShowSuccessIndicator.value = false;
    createTokenError.value =
      'Token creation failed, response is missing the token data: ' + res.data;
  }
  creatingToken.value = false;
});

// Handle any errors during token creation.
onCreateTokenError((err) => {
  createTokenError.value = err.message;
  createTokenSuccess.value = false;
  createTokenShowSuccessIndicator.value = false;
  creatingToken.value = false;
});

// Function to handle token creation form submission.
function handleCreateToken() {
  // Clear previous messages
  createTokenError.value = null;
  createTokenSuccess.value = false;
  createTokenShowSuccessIndicator.value = false;
  creatingToken.value = true;

  // Generate random key
  newTokenValue.value = genApiKey();

  // Build input object (adjust field names as needed).
  createApiKey({
    keyName: newTokenName.value,
    keyValue: newTokenValue.value,
    permissions: newTokenPermissions.value,
    description: newTokenDescription.value,
    expiresAt: newTokenExpiration.value
      ? new Date(newTokenExpiration.value)
      : null,
  });
}

function handleDismissToken() {
  createdTokenValue.value = null;
  createdTokenName.value = null;
  emits('apiKeyDismissed');
}

function copyTokenToClipboard(token: string | null) {
  if (!token) {
    console.error('No token to copy');
    return;
  }
  const clip = useClipboard();
  clip.copy(token);
  showCopyTokenTooltip.value = true;

  // Hide the tooltip after 2 seconds
  setTimeout(() => {
    showCopyTokenTooltip.value = false;
  }, 2000);
}
</script>

<template>
  <div>
    <b-form @submit.prevent="handleCreateToken" v-if="!createdTokenValue">
      <b-form-group label="Token Name" label-for="token-name" class="mt-3">
        <b-form-input
          id="token-name"
          name="tokenName"
          v-model="newTokenName"
          type="text"
          required
        />
      </b-form-group>

      <b-form-group
        label="Token Description"
        label-for="token-description"
        class="mt-3"
      >
        <b-form-textarea
          id="token-description"
          v-model="newTokenDescription"
          type="text"
        ></b-form-textarea>
      </b-form-group>

      <b-form-group
        label="Permissions"
        label-for="token-permissions"
        class="mt-3"
      >
        <select
          id="token-permissions"
          v-model="newTokenPermissions"
          class="form-control"
        >
          <option value="read">Read</option>
          <option value="write">Write</option>
        </select>
      </b-form-group>

      <b-form-group
        label="Expiration Date (optional)"
        label-for="token-expiration"
        class="mt-3"
      >
        <b-form-input
          id="token-expiration"
          v-model="newTokenExpiration"
          type="date"
        />
      </b-form-group>
      <b-button
        type="submit"
        variant="outline-dark"
        class="mt-3"
        :disabled="creatingToken"
      >
        <span v-if="creatingToken">
          Creating... <b-spinner small label="Creating"></b-spinner>
        </span>
        <span v-else-if="createTokenShowSuccessIndicator">
          Token created
          <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
        </span>
        <span v-else>Create token</span>
      </b-button>
    </b-form>

    <!-- Show the key for the last time on success -->
    <div v-if="createdTokenValue" variant="danger" class="mt-3">
      <p>
        <strong>Access token created successfully!</strong> Copy the token now
        as it will not be shown again.
      </p>
      <b-input-group :prepend="createdTokenName">
        <b-form-input
          id="created-token-value"
          type="password"
          v-model="createdTokenValue"
          readonly
        />
        <template #append>
          <b-button
            id="copy-button-token"
            variant="outline-dark"
            @click="copyTokenToClipboard(createdTokenValue)"
          >
            <font-awesome-icon :icon="['fa', 'copy']" />
          </b-button>
          <b-tooltip
            v-if="showCopyTokenTooltip"
            v-model="showCopyTokenTooltip"
            ref="tokenTooltip"
            target="copy-button-token"
            placement="top"
          >
            Access token copied
          </b-tooltip>
          <b-button
            id="hide-button-token"
            variant="outline-dark"
            @click="handleDismissToken"
          >
            <font-awesome-icon :icon="['fa', 'xmark']" />
          </b-button>
          <b-tooltip
            ref="hideTokenTooltip"
            target="hide-button-token"
            placement="top"
          >
            Hide access token
          </b-tooltip>
        </template>
      </b-input-group>
    </div>

    <!-- Eventual error should be shown in b-alert component -->
    <b-alert
      v-model="createTokenError"
      variant="danger"
      dismissible
      @dismissed="createTokenError = null"
      class="mt-3"
    >
      {{ createTokenError }}
    </b-alert>
  </div>
</template>
<style scoped></style>
